import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Card,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Tag,
  DatePicker,
  Button,
  TablePaginationConfig,
} from 'antd';
import { getMonitoringTargets } from '../../services/monitoringService';
import { getLeadsUsers } from '../../services/leadService';
import { MonitoringTarget, GetMonitoringTargetsParams } from '../../types/monitoring';
import { SalesUser } from '../../types';
import { useDebounce } from '../../utils/hooks';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const TaskLeadPage: React.FC = () => {
  const [tasks, setTasks] = useState<MonitoringTarget[]>([]);
  const [salesUsers, setSalesUsers] = useState<SalesUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState<Omit<GetMonitoringTargetsParams, 'page' | 'page_size'>>({
    lead_name: '',
    user_id: undefined,
    status: undefined,
    start_date: '',
    end_date: '',
  });

  const debouncedSearch = useDebounce(filters.lead_name, 500);

  const fetchSalesUsers = async () => {
    try {
      const response = await getLeadsUsers();
      setSalesUsers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch sales users:', error);
    }
  };

  const fetchTasks = useCallback(async (params: GetMonitoringTargetsParams) => {
    setLoading(true);
    try {
      const response = await getMonitoringTargets(params);
      const responseData = response.data.data || [];
      const paginationData = response.data.pagination;

      setTasks(responseData);

      if (paginationData) {
        setPagination(prev => ({
          ...prev,
          total: paginationData.total_items,
          current: paginationData.current_page,
          pageSize: paginationData.page_size,
        }));
      } else {
        // Fallback if pagination object is missing
        setPagination(prev => ({
          ...prev,
          total: responseData.length,
          current: 1,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSalesUsers();
  }, []);

  const { current: currentPage, pageSize } = pagination;

  useEffect(() => {
    const params: GetMonitoringTargetsParams = {
      page: currentPage,
      page_size: pageSize,
      lead_name: debouncedSearch,
      user_id: filters.user_id,
      status: filters.status,
      start_date: filters.start_date,
      end_date: filters.end_date,
    };
    // Remove empty filters to keep the URL clean
    Object.keys(params).forEach(key => {
      if (!params[key as keyof GetMonitoringTargetsParams]) {
        delete params[key as keyof GetMonitoringTargetsParams];
      }
    });
    fetchTasks(params);
  }, [
    debouncedSearch,
    filters.user_id,
    filters.status,
    filters.start_date,
    filters.end_date,
    currentPage,
    pageSize,
    fetchTasks
  ]);

  const handleFilterChange = (key: string, value: any) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDateChange = (dates: any, dateStrings: [string, string]) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setFilters(prev => ({
      ...prev,
      start_date: dateStrings[0] || '',
      end_date: dateStrings[1] || '',
    }));
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination(newPagination);
  };

  const resetFilters = () => {
    setFilters({
      lead_name: '',
      user_id: undefined,
      status: undefined,
      start_date: '',
      end_date: '',
    });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const columns = [
    {
      title: 'Nama Sales',
      dataIndex: 'user_name',
      key: 'user_name',
    },
    {
      title: 'Nama Lead',
      dataIndex: 'lead_name',
      key: 'lead_name',
    },
    {
      title: 'Next Task',
      dataIndex: 'stage',
      key: 'stage',
    },
    {
      title: 'Jatuh Tempo',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (text: string) => dayjs(text).format('DD MMMM YYYY HH:mm'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = status === 'pending' ? 'orange' : 'red';
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
  ];

  const dateValue: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null =
    filters.start_date && filters.end_date
      ? [dayjs(filters.start_date), dayjs(filters.end_date)]
      : null;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Monitoring Task Lead
          </Title>
        </Col>
        <Col>
          <Button onClick={resetFilters}>
            Reset Filter
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={5}>
            <Search
              placeholder="Cari lead..."
              value={filters.lead_name}
              onChange={(e) => handleFilterChange('lead_name', e.target.value)}
              style={{ width: '100%' }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              showSearch
              placeholder="Filter by Sales"
              value={filters.user_id}
              onChange={(value) => handleFilterChange('user_id', value)}
              style={{ width: '100%' }}
              allowClear
              filterOption={(input, option) =>
                (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
              }
            >
              {salesUsers.map(user => (
                <Option key={user.id} value={user.id}>{user.full_name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Filter by Status"
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={10}>
            <RangePicker
              value={dateValue}
              style={{ width: '100%' }}
              onChange={handleDateChange}
              format="YYYY-MM-DD"
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="target_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
};

export default TaskLeadPage;
